import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FeatureComparison = () => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const featureCategories = [
    {
      name: 'Core Features',
      features: [
        {
          name: 'SAP Best Practice Access',
          free: '3 scope items',
          pro: 'All scope items',
          enterprise: 'All scope items + Custom'
        },
        {
          name: 'Questionnaire Responses',
          free: '10/month',
          pro: 'Unlimited',
          enterprise: 'Unlimited'
        },
        {
          name: 'Document Generation',
          free: 'Basic templates',
          pro: 'Advanced templates',
          enterprise: 'Custom templates'
        },
        {
          name: 'AI Assistance',
          free: 'Limited',
          pro: 'Full access',
          enterprise: 'Priority + Custom models'
        }
      ]
    },
    {
      name: 'Collaboration',
      features: [
        {
          name: 'Team Members',
          free: '1 user',
          pro: 'Up to 10 users',
          enterprise: 'Unlimited users'
        },
        {
          name: 'Project Workspaces',
          free: '1 project',
          pro: '5 projects',
          enterprise: 'Unlimited projects'
        },
        {
          name: 'Digital Signatures',
          free: false,
          pro: true,
          enterprise: true
        },
        {
          name: 'Real-time Collaboration',
          free: false,
          pro: true,
          enterprise: true
        }
      ]
    },
    {
      name: 'Enterprise Features',
      features: [
        {
          name: 'SSO Integration',
          free: false,
          pro: false,
          enterprise: true
        },
        {
          name: 'Advanced Security',
          free: false,
          pro: 'Standard',
          enterprise: 'Enterprise-grade'
        },
        {
          name: 'API Access',
          free: false,
          pro: 'Limited',
          enterprise: 'Full access'
        },
        {
          name: 'Dedicated Support',
          free: false,
          pro: false,
          enterprise: true
        }
      ]
    }
  ];

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Icon name="Check" size={20} className="text-success" />;
    }
    if (value === false) {
      return <Icon name="X" size={20} className="text-muted-foreground" />;
    }
    return <span className="text-sm text-foreground">{value}</span>;
  };

  const visibleCategories = showAllFeatures ? featureCategories : featureCategories?.slice(0, 2);

  return (
    <div className="mt-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Compare Plans & Features
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Detailed comparison of features across all pricing tiers to help you choose the right plan.
        </p>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-4 gap-4 p-6 bg-muted/30 border-b border-border">
          <div className="font-semibold text-foreground">
            Features
          </div>
          <div className="text-center font-semibold text-foreground">
            Free
          </div>
          <div className="text-center font-semibold text-foreground">
            Pro
          </div>
          <div className="text-center font-semibold text-foreground">
            Enterprise
          </div>
        </div>

        {/* Feature Categories */}
        {visibleCategories?.map((category, categoryIndex) => (
          <div key={categoryIndex}>
            {/* Category Header */}
            <div className="px-6 py-4 bg-muted/10 border-b border-border">
              <h3 className="font-semibold text-foreground">
                {category?.name}
              </h3>
            </div>

            {/* Features */}
            {category?.features?.map((feature, featureIndex) => (
              <div 
                key={featureIndex}
                className="grid grid-cols-4 gap-4 p-4 border-b border-border last:border-b-0 hover:bg-muted/20 nav-transition"
              >
                <div className="font-medium text-foreground">
                  {feature?.name}
                </div>
                <div className="text-center">
                  {renderFeatureValue(feature?.free)}
                </div>
                <div className="text-center">
                  {renderFeatureValue(feature?.pro)}
                </div>
                <div className="text-center">
                  {renderFeatureValue(feature?.enterprise)}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Show More/Less Button */}
        <div className="p-6 text-center border-t border-border">
          <button
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 nav-transition"
          >
            <span>
              {showAllFeatures ? 'Show Less Features' : 'Show All Features'}
            </span>
            <Icon 
              name={showAllFeatures ? "ChevronUp" : "ChevronDown"} 
              size={16} 
            />
          </button>
        </div>
      </div>
      {/* Usage Limits */}
      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4">
            Storage & Limits
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Document Storage</span>
              <span className="text-foreground">1GB / 10GB / Unlimited</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">File Upload Size</span>
              <span className="text-foreground">10MB / 100MB / 1GB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Version History</span>
              <span className="text-foreground">30 days / 1 year / Unlimited</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4">
            Support & SLA
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Support Channel</span>
              <span className="text-foreground">Community / Email / Phone</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Response Time</span>
              <span className="text-foreground">48h / 24h / 4h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Uptime SLA</span>
              <span className="text-foreground">99% / 99.5% / 99.9%</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4">
            Training & Onboarding
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Documentation</span>
              <span className="text-foreground">Basic / Full / Premium</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Training Sessions</span>
              <span className="text-foreground">Self-serve / Group / 1-on-1</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Implementation</span>
              <span className="text-foreground">DIY / Guided / Managed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureComparison;